import { store } from "../..";
import { ImageData, LabelName, LabelRect } from "../../store/labels/types";
import {
  updateActiveLabelId,
  updateHighlightedLabelId,
  updateImageDataById,
  updateImageDataBatch,
  updateImageDataByIdwithoutundo,
  updatePendingTrackId,
} from "../../store/labels/actionCreators";
import { LabelsSelector } from "../../store/selectors/LabelsSelector";
import { EditorActions } from "./EditorActions";
import { LabelStatus } from "../../data/enums/LabelStatus";
import { getCokkie } from "../../views/login/Login";
import { v4 as uuidv4 } from "uuid";

export type TrackSummary = {
  trackId: string;
  labelId: string | null;
  labelName: string;
  frameCount: number;
  displayNumber: number;
};

export type TrackMergeResult =
  | { trackId: string; error: null }
  | { trackId: null; error: string };

export type TrackDeleteResult = {
  deletedFrames: number;
  deletedTracks: number;
  earlierTracks: number;
  error: string | null;
};

let reservedTrackIds: number[] = [];
let reservationTaskId: string | null = null;
let reservationInFlight: Promise<void> | null = null;
let trackListCache: {
  images: ImageData[];
  labels: LabelName[];
  taskId: string;
  tracks: TrackSummary[];
} | null = null;
const displayNumberMaps = new Map<string, Record<string, number>>();

const normalizeTrackId = (value: unknown): string => String(value ?? "").trim();

const trackIdReservationCacheKey = (taskId: string): string =>
  `annotation-track-id-reservations:${taskId}`;

const readTrackIdReservations = (taskId: string): number[] => {
  try {
    const cached = JSON.parse(
      localStorage.getItem(trackIdReservationCacheKey(taskId)) || "[]",
    );
    return Array.isArray(cached)
      ? cached.filter((id: unknown) => Number.isSafeInteger(id) && Number(id) > 0)
      : [];
  } catch {
    return [];
  }
};

const storeTrackIdReservations = (taskId: string, reservations: number[]): void => {
  try {
    localStorage.setItem(
      trackIdReservationCacheKey(taskId),
      JSON.stringify(reservations),
    );
  } catch (error) {
    console.warn("Unable to persist reserved track IDs", error);
  }
};

const displayNumberCacheKey = (taskId: string, labelId: string | null): string =>
  `annotation-track-display-numbers:${taskId}:${labelId || "unlabeled"}`;

const readDisplayNumberMap = (
  taskId: string,
  labelId: string | null,
): Record<string, number> => {
  try {
    const cached = JSON.parse(
      localStorage.getItem(displayNumberCacheKey(taskId, labelId)) || "{}",
    );
    if (!cached || typeof cached !== "object" || Array.isArray(cached)) return {};
    return Object.fromEntries(
      Object.entries(cached).filter(([, value]) =>
        Number.isSafeInteger(value) && Number(value) > 0,
      ),
    );
  } catch {
    return {};
  }
};

export class TrackActions {
  public static isSequenceReady(): boolean {
    const images = LabelsSelector.getImagesData();
    return images.length > 0 && images.every(
      (image) => image.annotationLoaded === true && image.annotationLoadState === "loaded"
    );
  }

  private static requireSequenceReady(): boolean {
    if (TrackActions.isSequenceReady()) return true;
    window.alert("Wait for every frame's annotations to load before editing tracks.");
    return false;
  }

  public static listTracks(): TrackSummary[] {
    const imagesData = LabelsSelector.getImagesData() || [];
    const labelNames: LabelName[] = LabelsSelector.getLabelNames() || [];
    const taskId = String(store.getState().general.projectData?.id || "");
    if (trackListCache?.images === imagesData &&
        trackListCache.labels === labelNames && trackListCache.taskId === taskId) {
      return trackListCache.tracks;
    }
    const byTrack = new Map<string, TrackSummary>();
    const namesById = new Map(labelNames.map((label) => [label.id, label.name]));

    imagesData.forEach((image) => {
      (image.labelRects || []).forEach((rect) => {
        const trackId = normalizeTrackId(rect?.trackId);
        if (!trackId) return;
        const existing = byTrack.get(trackId);
        if (existing) {
          existing.frameCount += 1;
          return;
        }
        const labelName = namesById.get(rect.labelId || "") || "unlabeled";
        byTrack.set(trackId, {
          trackId,
          labelId: rect.labelId ?? null,
          labelName,
          frameCount: 1,
          displayNumber: 0,
        });
      });
    });

    const tracks = Array.from(byTrack.values());
    const tracksByLabel = new Map<string, TrackSummary[]>();
    tracks.forEach((track) => {
      const labelKey = track.labelId || "unlabeled";
      tracksByLabel.set(labelKey, [...(tracksByLabel.get(labelKey) || []), track]);
    });
    tracksByLabel.forEach((labelTracks, labelKey) => {
      const labelId = labelKey === "unlabeled" ? null : labelKey;
      const cacheKey = displayNumberCacheKey(taskId, labelId);
      let displayNumbers = displayNumberMaps.get(cacheKey);
      if (!displayNumbers) {
        displayNumbers = taskId ? readDisplayNumberMap(taskId, labelId) : {};
        displayNumberMaps.set(cacheKey, displayNumbers);
      }
      const usedNumbers = new Set(Object.values(displayNumbers));
      let nextNumber = 1;
      let numbersChanged = false;
      labelTracks.forEach((track) => {
        if (!displayNumbers[track.trackId]) {
          while (usedNumbers.has(nextNumber)) nextNumber += 1;
          displayNumbers[track.trackId] = nextNumber;
          usedNumbers.add(nextNumber);
          numbersChanged = true;
        }
        track.displayNumber = displayNumbers[track.trackId];
        nextNumber = Math.max(nextNumber, track.displayNumber + 1);
      });
      if (taskId && numbersChanged) {
        try {
          localStorage.setItem(cacheKey, JSON.stringify(displayNumbers));
        } catch (error) {
          console.warn("Unable to persist track display numbers", error);
        }
      }
    });
    trackListCache = { images: imagesData, labels: labelNames, taskId, tracks };
    return tracks;
  }

  public static displayName(trackId: string | null | undefined): string {
    if (!trackId) return "";
    const track = TrackActions.listTracks().find((item) => item.trackId === trackId);
    if (!track) return `Track ${trackId}`;
    return `${track.labelName} #${track.displayNumber}`;
  }

  /** Reserve a small batch of numeric IDs and reuse it across editor reloads. */
  public static async ensureTrackIdReservation(): Promise<void> {
    const taskId = String(store.getState().general.projectData?.id || "");
    if (!taskId || !TrackActions.isSequenceReady()) return;
    if (reservationTaskId !== taskId) {
      reservationTaskId = taskId;
      reservedTrackIds = readTrackIdReservations(taskId);
    }
    if (reservedTrackIds.length > 0) return;
    if (reservationInFlight) {
      await reservationInFlight;
      return TrackActions.ensureTrackIdReservation();
    }

    reservationInFlight = (async () => {
      const tracks = TrackActions.listTracks();
      const numericMaximum = Math.max(
        tracks.reduce((maximum, track) =>
          /^[1-9]\d*$/.test(track.trackId)
            ? Math.max(maximum, Number(track.trackId))
            : maximum, 0),
        tracks.length,
      );
      const response = await fetch(
        `${window["ENV"].API_URL}/annotation/tasks/${taskId}/track-ids`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getCokkie("jwtToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ count: 16, minimum: numericMaximum }),
        },
      );
      if (!response.ok) throw new Error(`Track ID reservation failed: ${response.status}`);
      const payload = await response.json();
      const start = Number(payload?.start);
      const end = Number(payload?.end);
      if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end) || start < 1 || end < start) {
        throw new Error("Track ID reservation returned an invalid range");
      }
      reservedTrackIds = Array.from({ length: end - start + 1 }, (_, offset) => start + offset);
      reservationTaskId = taskId;
      storeTrackIdReservations(taskId, reservedTrackIds);
    })().finally(() => {
      reservationInFlight = null;
    });
    return reservationInFlight;
  }

  public static takeNextTrackId(): string | null {
    const taskId = String(store.getState().general.projectData?.id || "");
    if (taskId !== reservationTaskId || reservedTrackIds.length === 0) return null;
    const nextId = String(reservedTrackIds.shift());
    storeTrackIdReservations(taskId, reservedTrackIds);
    void TrackActions.ensureTrackIdReservation().catch((error) =>
      console.error("Unable to replenish track IDs", error),
    );
    return nextId;
  }

  /** Place a track rectangle and seed later frames that do not yet contain it. */
  public static placeTrackRect(sourceRect: LabelRect): LabelRect | null {
    if (!sourceRect?.trackId || !TrackActions.requireSequenceReady()) return null;

    const image = LabelsSelector.getActiveImageData();
    if (!image) return null;
    const rects = image.labelRects || [];
    const existing = rects.find((rect) => rect.trackId === sourceRect.trackId);
    const placedRect = { ...sourceRect, id: existing?.id ?? sourceRect.id };
    const nextImage: ImageData = {
      ...image,
      labelRects: existing
        ? rects.map((rect) => rect.id === existing.id ? placedRect : rect)
        : [...rects, placedRect],
      saved: false,
    };
    store.dispatch(updateImageDataById(image.image_id, nextImage));

    const imagesData = LabelsSelector.getImagesData() || [];
    const activeIndex = imagesData.findIndex((frame) => frame.image_id === image.image_id);
    imagesData.forEach((frame, index) => {
      if (index <= activeIndex) return;
      const frameRects = frame.labelRects || [];
      if (frameRects.some((rect) => rect.trackId === placedRect.trackId)) return;
      const propagatedRect: LabelRect = {
        ...placedRect,
        id: uuidv4(),
        rect: { ...placedRect.rect },
      };
      store.dispatch(updateImageDataByIdwithoutundo(frame.image_id, {
        ...frame,
        labelRects: [...frameRects, propagatedRect],
        saved: false,
      }));
    });

    EditorActions.fullRender();
    return placedRect;
  }

  /** Remove selected track boxes from the open frame onward in one state update. */
  public static deleteTracksForward(trackIds: string[]): TrackDeleteResult {
    const failure = (error: string): TrackDeleteResult => ({
      deletedFrames: 0, deletedTracks: 0, earlierTracks: 0, error,
    });
    const selected = new Set(trackIds.map(normalizeTrackId).filter(Boolean));
    if (selected.size === 0) return failure("Select at least one track to delete forward.");
    if (!TrackActions.isSequenceReady()) return failure("Wait for every frame to load before deleting tracks.");

    const images = store.getState().labels.imagesData;
    const fromIndex = store.getState().labels.activeImageIndex ?? -1;
    if (!Number.isInteger(fromIndex) || fromIndex < 0 || fromIndex >= images.length) {
      return failure("The current frame is unavailable. Open a frame and try again.");
    }
    const changed: ImageData[] = [];
    const affectedTracks = new Set<string>();
    const earlierTracks = new Set<string>();
    images.forEach((image, index) => {
      const rects = image.labelRects || [];
      if (index < fromIndex) {
        rects.forEach(rect => {
          const id = normalizeTrackId(rect.trackId);
          if (selected.has(id)) earlierTracks.add(id);
        });
        return;
      }
      const remaining = rects.filter(rect => {
        const id = normalizeTrackId(rect.trackId);
        if (!selected.has(id)) return true;
        affectedTracks.add(id);
        return false;
      });
      if (remaining.length !== rects.length) {
        changed.push({ ...image, labelRects: remaining, saved: false });
      }
    });
    if (changed.length === 0) {
      return failure("Selected tracks have no boxes on this or later frames. Boxes on earlier frames remain.");
    }

    const openImageId = images[fromIndex].image_id;
    store.dispatch(updateImageDataBatch(changed, openImageId));
    const latest = store.getState().labels.imagesData;
    const remaining = latest.slice(fromIndex).some(image =>
      (image.labelRects || []).some(rect => selected.has(normalizeTrackId(rect.trackId)))
    );
    if (remaining) return failure("Some selected boxes remain after deletion. Please try again.");

    if (selected.has(normalizeTrackId(LabelsSelector.getPendingTrackId()))) {
      store.dispatch(updatePendingTrackId(null));
    }
    const currentImage = latest[fromIndex];
    const activeId = LabelsSelector.getActiveLabelId();
    const highlightedId = LabelsSelector.getHighlightedLabelId();
    if (activeId && !(currentImage.labelRects || []).some(rect => String(rect.id) === String(activeId))) {
      store.dispatch(updateActiveLabelId(null));
    }
    if (highlightedId && !(currentImage.labelRects || []).some(rect => String(rect.id) === String(highlightedId))) {
      store.dispatch(updateHighlightedLabelId(null));
    }
    EditorActions.fullRender();
    return {
      deletedFrames: changed.length,
      deletedTracks: affectedTracks.size,
      earlierTracks: earlierTracks.size,
      error: null,
    };
  }

  /** Apply class label to every frame that has this trackId. */
  public static updateTrackLabel(
    trackId: string,
    labelId: string | null
  ): void {
    if (!trackId || !TrackActions.requireSequenceReady()) return;

    const imagesData = LabelsSelector.getImagesData() || [];
    const activeIndex = LabelsSelector.getActiveImageIndex();

    imagesData.forEach((image, index) => {
      const rects = image.labelRects || [];
      let changed = false;
      const nextRects = rects.map((rect) => {
        if (rect.trackId !== trackId) return rect;
        if (rect.labelId === labelId) return rect;
        changed = true;
        return {
          ...rect,
          labelId,
          status: LabelStatus.ACCEPTED,
        };
      });
      if (!changed) return;

      const newImageData: ImageData = {
        ...image,
        labelRects: nextRects,
        saved: false,
      };

      if (index === activeIndex) {
        store.dispatch(updateImageDataById(image.image_id, newImageData));
      } else {
        store.dispatch(
          updateImageDataByIdwithoutundo(image.image_id, newImageData)
        );
      }
    });

    EditorActions.fullRender();
  }

  /** Merge non-overlapping tracks into the ID present on the earliest frame. */
  public static mergeTracks(trackIds: string[]): TrackMergeResult {
    const unique = Array.from(new Set(trackIds.map(normalizeTrackId).filter(Boolean)));
    if (unique.length < 2) {
      return { trackId: null, error: "Select at least two tracks to merge." };
    }
    if (!TrackActions.isSequenceReady()) {
      return { trackId: null, error: "Wait for every frame's annotations to load before merging tracks." };
    }

    const mergeSet = new Set(unique);
    const imagesData = store.getState().labels.imagesData;
    const selectedRects = imagesData.flatMap((image) =>
      (image.labelRects || []).filter((rect) => mergeSet.has(normalizeTrackId(rect.trackId)))
    );
    const foundIds = new Set(selectedRects.map((rect) => normalizeTrackId(rect.trackId)));
    if (foundIds.size !== unique.length) {
      return { trackId: null, error: "One of the selected tracks no longer exists. Select the tracks again." };
    }
    const conflictingFrameNumbers = imagesData.flatMap((image, index) =>
      (image.labelRects || []).filter((rect) => mergeSet.has(normalizeTrackId(rect.trackId))).length > 1
        ? [index + 1]
        : []
    );
    if (conflictingFrameNumbers.length > 0) {
      const examples = conflictingFrameNumbers
        .slice(0, 3)
        .map((frameNumber) => `#${frameNumber}`)
        .join(", ");
      const remaining = conflictingFrameNumbers.length - 3;
      return {
        trackId: null,
        error: `Cannot merge: selected tracks have boxes on the same frames (${examples}${remaining > 0 ? ` and ${remaining} more` : ""}).`,
      };
    }
    const labelIds = new Set(selectedRects.map((rect) => String(rect.labelId ?? "").trim()));
    if (labelIds.size > 1) {
      return { trackId: null, error: "Cannot merge tracks with different classes." };
    }
    if (labelIds.has("")) {
      return { trackId: null, error: "Select a class for each track before merging." };
    }
    const canonical = normalizeTrackId(selectedRects[0].trackId);

    const changedImages: ImageData[] = [];
    imagesData.forEach((image) => {
      const rects = image.labelRects || [];
      let imageChanged = false;
      const nextRects = rects.map((rect) => {
        if (mergeSet.has(normalizeTrackId(rect.trackId)) && rect.trackId !== canonical) {
          imageChanged = true;
          return { ...rect, trackId: canonical };
        }
        return rect;
      });
      if (!imageChanged) return;

      changedImages.push({
        ...image,
        labelRects: nextRects,
        saved: false,
      });
    });

    const openImageId = imagesData[store.getState().labels.activeImageIndex ?? -1]?.image_id ?? null;
    store.dispatch(updateImageDataBatch(changedImages, openImageId));
    const latest = store.getState().labels.imagesData;
    const remaining = latest.some(image =>
      (image.labelRects || []).some(rect =>
        mergeSet.has(normalizeTrackId(rect.trackId)) && normalizeTrackId(rect.trackId) !== canonical
      )
    );
    const mergedCount = latest.reduce((count, image) => count +
      (image.labelRects || []).filter(rect => normalizeTrackId(rect.trackId) === canonical).length, 0);
    if (remaining || mergedCount !== selectedRects.length) {
      return { trackId: null, error: "Merge could not be verified. Reload the task before trying again." };
    }

    const pendingTrackId = LabelsSelector.getPendingTrackId();
    if (pendingTrackId && mergeSet.has(normalizeTrackId(pendingTrackId))) {
      store.dispatch(updatePendingTrackId(null));
    }
    EditorActions.fullRender();
    return { trackId: canonical, error: null };
  }

  public static shortTrackId(trackId: string | null | undefined): string {
    if (!trackId) return "";
    const track = TrackActions.listTracks().find((item) => item.trackId === trackId);
    return String(track?.displayNumber ?? trackId);
  }
}
